import React, { useState } from 'react';
import styles from './SitePreviewContent.scss';
import Success from 'assets/svgs/success.svg';
import { useMsId } from 'stores/appStore';
import { useSessionId } from 'hooks';
import { redirectToPremium } from 'utils/premium';
import { assertString } from '@wix/gg-common-lib/utils';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import { redirectToDashboard } from 'utils/dashboard';
import { useIsMobile } from '@wix/gg-common-lib/hooks';
import type { SitePreviewViewModel } from '../sitePreviewViewModel';

interface SitePreviewContentProps {
  viewModel: SitePreviewViewModel;
  productCount: number;
}

interface HeaderProps {
  titleKey: string;
  subtitleKey: string;
  productCount: number;
}

interface CouponCodeButtonProps {
  couponCode: string;
  isCopied: boolean;
  onCopy: () => void;
}

interface OfferCardProps {
  viewModel: SitePreviewViewModel;
  isCopied: boolean;
  isLoading: boolean;
  isCtaDisabled: boolean;
  onCopyCoupon: () => void;
  onCtaClick: () => void;
}

function Header({ titleKey, subtitleKey, productCount }: HeaderProps) {
  return (
    <>
      <p className={styles.sitePreviewContentTitle}>
        <Trans
          i18nKey={titleKey}
          components={{
            br: <br />,
            strong: <strong />,
          }}
        />
      </p>
      <p className={styles.sitePreviewContentSubtitle}>
        <Trans
          i18nKey={subtitleKey}
          values={{ count: productCount }}
          components={{
            br: <br />,
          }}
        />
      </p>
    </>
  );
}

function CouponCodeButton({ couponCode, isCopied, onCopy }: CouponCodeButtonProps) {
  const { t } = useTranslation();

  return (
    <button
      type="button"
      className={styles.sitePreviewContentBannerCoupon}
      onClick={onCopy}
      aria-label={t('site_preview.premium.offer.coupon.label')}
    >
      <span className={styles.sitePreviewContentBannerCouponLabel}>
        {isCopied ? t('site_preview.premium.offer.coupon.copied') : t('site_preview.premium.offer.coupon.label')}
      </span>
      <span className={styles.sitePreviewContentBannerCouponCode}>{couponCode}</span>
    </button>
  );
}

function OfferCard({ viewModel, isCopied, isLoading, isCtaDisabled, onCopyCoupon, onCtaClick }: OfferCardProps) {
  const { t } = useTranslation();
  const { offerCard } = viewModel;

  return (
    <div className={styles.sitePreviewContentBanner}>
      <div className={styles.sitePreviewContentBannerInfo}>
        {offerCard.urgencyKey && <p className={styles.sitePreviewContentBannerUrgency}>{t(offerCard.urgencyKey)}</p>}
        {offerCard.discountKey && offerCard.discountPercent && (
          <p className={styles.sitePreviewContentBannerDiscount}>
            {t(offerCard.discountKey, { discountPercent: offerCard.discountPercent })}
          </p>
        )}
        {offerCard.descriptionKey && (
          <p className={styles.sitePreviewContentBannerDescription}>{t(offerCard.descriptionKey)}</p>
        )}
      </div>
      {viewModel.couponCode && (
        <CouponCodeButton couponCode={viewModel.couponCode} isCopied={isCopied} onCopy={onCopyCoupon} />
      )}
      <button className={styles.sitePreviewContentBannerCta} onClick={onCtaClick} disabled={isLoading || isCtaDisabled}>
        {t(offerCard.buttonKey)}
      </button>
      {(offerCard.footerPrefixKey || offerCard.footerHighlightKey) && (
        <div className={styles.sitePreviewContentBannerFooter}>
          {offerCard.footerPrefixKey && (
            <p className={styles.sitePreviewContentBannerFooterPrefix}>{t(offerCard.footerPrefixKey)}</p>
          )}
          {offerCard.footerHighlightKey && (
            <p className={styles.sitePreviewContentBannerFooterHighlight}>{t(offerCard.footerHighlightKey)}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function SitePreviewContent({ viewModel, productCount }: SitePreviewContentProps) {
  const msId = useMsId();
  const sessionId = useSessionId();

  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const isMobile = useIsMobile();

  const handleCtaClick = async () => {
    try {
      setIsLoading(true);
      assertString(msId);
      if (viewModel.ctaTarget === 'dashboard') {
        await redirectToDashboard({ metaSiteId: msId });
      } else {
        await redirectToPremium({ metaSiteId: msId, sessionId });
      }
    } catch {
      setIsLoading(false);
    }
  };

  const handleCopyCoupon = async () => {
    try {
      assertString(viewModel.couponCode);
      await navigator.clipboard.writeText(viewModel.couponCode);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (error) {
      console.error('Failed to copy coupon code:', error);
    }
  };

  return (
    <div className={styles.sitePreviewContent}>
      {!(viewModel.couponCode && isMobile) && <Success />}
      <Header titleKey={viewModel.titleKey} subtitleKey={viewModel.subtitleKey} productCount={productCount} />
      <OfferCard
        viewModel={viewModel}
        isCopied={isCopied}
        isLoading={isLoading}
        isCtaDisabled={!msId}
        onCopyCoupon={handleCopyCoupon}
        onCtaClick={handleCtaClick}
      />
    </div>
  );
}
